<template>
  <div
    class="db-wrapper-field"
    :class="[
      $attrs.class,
      {
        'db-has-error': $slots.error,
        'db-has-success': isSuccess,
        'db-has-icon': $slots.prepend,
      },
    ]"
  >
    <span
      v-if="$slots.prepend"
      class="db-field-prepend"
    >
      <slot name="prepend" />
    </span>
    <textarea
      :id="uid"
      class="db-textarea"
      type="text"
      :aria-describedby="describedbyAttr"
      v-bind="inputAttrs"
      :value="modelValue"
      v-on:input="onInput"
    />
    <label
      class="db-label"
      :for="uid"
    >
      <span>
        <slot />
        <span
          v-if="inputAttrs?.required"
          class="db-input-required-indicator"
        >
          *
        </span>
      </span>
    </label>
  </div>
  <p
    v-if="inputAttrs?.maxlength"
    :id="`maxlength-${uid}`"
    class="db-input-hint db-input-charleft"
  >
    {{ `${charsLeft} / ${inputAttrs.maxlength}` }}
  </p>
  <p
    v-if="$slots.hint"
    :id="`hint-${uid}`"
    class="db-input-hint"
  >
    <slot name="hint" />
  </p>
  <p
    v-if="$slots.error"
    :id="`error-${uid}`"
    class="db-input-error"
  >
    <slot name="error" />
  </p>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import { computed, useSlots } from 'vue'
import type { TextareaHTMLAttributes } from 'vue'

const props = defineProps<{
  /** Is the field in a success state */
  isSuccess?: boolean
  /** binds /w `v-model` directive */
  modelValue: string
  /** TextareaHTMLAttributes /w `id` omitted. `maxlength` key triggers char counter */
  inputAttrs?: Omit<TextareaHTMLAttributes, 'id'>
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: string): void
}>()

/** Unique id for label / input binding */
const uid = uuidv4()

/** Emit event for v-model */
function onInput(e: Event) {
  const target = e?.target as HTMLInputElement
  emit('update:modelValue', target.value)
}

/** Computed `aria-describedby` attr */
const charsLeft = computed(() => {
  const maxlength = props.inputAttrs?.maxlength
  const charLength = props.modelValue.length
  if (maxlength) {
    return typeof maxlength === 'string' ? parseInt(maxlength) - charLength : maxlength - charLength
  }
  return null
})

/** Get the slots to check which is on */
const slots = useSlots()

/** Computed `aria-describedby` attr */
const describedbyAttr = computed(() => {
  const describedbyAttrs = [
    ...(props.inputAttrs?.maxlength ? [`maxlength-${uid}`] : []),
    ...(slots.hint !== undefined ? [`hint-${uid}`] : []),
    ...(slots.error !== undefined ? [`error-${uid}`] : []),
  ]

  return describedbyAttrs.length > 0 ? describedbyAttrs.toString().replace(',', ' ') : undefined
})
</script>

<style>
@import '@/assets/styles/commons/forms/_inputs-field.css';
@import '@/assets/styles/components/forms/_textarea.css';
</style>
